import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeftIcon, PhoneIcon, ChatBubbleLeftRightIcon, BeakerIcon, XMarkIcon, ArrowPathIcon, ClockIcon, MusicalNoteIcon, ChartBarIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { workflowService, type WorkflowRunSummary } from '../../services/workflowService';
import { fileService } from '../../services/fileService';
import { renderChatMarkdown } from '../../utility/renderMarkdownText';
import { Button } from '../ui/Button';

interface Props {
  open: boolean;
  workflowId?: string;
  workflowName?: string;
  onClose: () => void;
}

// Runs carry metadata at runtime even though the summary type omits it.
type ConversationRow = WorkflowRunSummary & { metadata?: any };
type TranscriptMsg = { id: string; senderType: string; message: string; createdAt: string };

const relativeTime = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const isTestCall = (row: ConversationRow) =>
  /^(test|verify)-/.test(String(row.externalSessionId || ''));

const channelMeta = (row: ConversationRow): { icon: React.ReactNode; label: string } => {
  const ch = String(row.channel || '').toLowerCase();
  if (isTestCall(row)) return { icon: <BeakerIcon className="h-4 w-4" />, label: 'Test call' };
  if (ch.includes('voice') || ch.includes('call')) return { icon: <PhoneIcon className="h-4 w-4" />, label: 'Voice call' };
  if (ch === 'whatsapp') return { icon: <DevicePhoneMobileIcon className="h-4 w-4" />, label: 'WhatsApp' };
  return { icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />, label: 'Chat' };
};

const formatDuration = (secs: number): string => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};

/** Call duration, most reliable source first: extraction metadata →
 *  completedAt−startedAt → updatedAt−startedAt. Returns '' if unknown. */
const durationSeconds = (row: ConversationRow): number | null => {
  const meta = row.metadata?.call?.duration_seconds;
  if (typeof meta === 'number' && meta > 0) return meta;
  const start = row.startedAt ? new Date(row.startedAt).getTime() : NaN;
  const end = row.completedAt
    ? new Date(row.completedAt).getTime()
    : row.updatedAt
      ? new Date(row.updatedAt).getTime()
      : NaN;
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return Math.round((end - start) / 1000);
  }
  return null;
};

const durationLabel = (row: ConversationRow): string => {
  const secs = durationSeconds(row);
  return secs && secs > 0 ? formatDuration(secs) : '';
};

type RunEvent = { id: string; type: string; payload: any; createdAt: string };
type StageTiming = { stage: string; ms: number };
type TurnStat = {
  turn: number;
  sttMs: number | null;
  totalMs: number | null;
  brainMs: number | null;
  ttsMs: number | null;
  stages: StageTiming[];
};

/** Sub-steps inside "Brain", in display order, with a label + bar colour. Any
 *  stage the backend emits that isn't listed here still shows, using a fallback. */
const STAGE_META: Record<string, { label: string; color: string }> = {
  router: { label: 'Routing', color: 'bg-sky-500' },
  end_check: { label: 'Call-end check', color: 'bg-amber-500' },
  retrieval: { label: 'Knowledge (RAG)', color: 'bg-violet-500' },
  can_answer: { label: 'Answerable check', color: 'bg-rose-500' },
  scheduler: { label: 'Booking agent', color: 'bg-indigo-500' },
  faq: { label: 'FAQ agent', color: 'bg-teal-500' },
  llm: { label: 'LLM answer', color: 'bg-indigo-500' },
};
const STAGE_ORDER = ['router', 'end_check', 'retrieval', 'can_answer', 'scheduler', 'faq', 'llm'];
const stageMeta = (stage: string) =>
  STAGE_META[stage] ?? { label: stage.replace(/_/g, ' '), color: 'bg-gray-400' };

const fmtMs = (ms: number | null): string =>
  ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

/** Roll the per-turn latency marks up into a breakdown per turn: total (T2−T0),
 *  brain (T1−T0), TTS (T2−T1), and the sub-steps inside Brain from stage_timing. */
const computeTurnStats = (events: RunEvent[]): TurnStat[] => {
  const byTurn = new Map<number, { sentence?: any; audio?: any; stages: Map<string, number> }>();
  for (const e of events) {
    const t = e.payload?.turn;
    if (typeof t !== 'number') continue;
    const g = byTurn.get(t) ?? { stages: new Map<string, number>() };
    if (e.type === 'turn_first_sentence') g.sentence = e.payload;
    else if (e.type === 'turn_first_audio') g.audio = e.payload;
    else if (e.type === 'stage_timing' && typeof e.payload?.ms === 'number' && typeof e.payload?.stage === 'string') {
      // Sum repeats within a turn (e.g. a retrieval fallback runs twice).
      g.stages.set(e.payload.stage, (g.stages.get(e.payload.stage) ?? 0) + e.payload.ms);
    }
    byTurn.set(t, g);
  }
  return Array.from(byTurn.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([turn, g]) => {
      const totalMs = typeof g.audio?.ms_since_user === 'number' ? g.audio.ms_since_user : null;
      const ttsMs = typeof g.audio?.ms_since_sentence === 'number' ? g.audio.ms_since_sentence : null;
      const brainMs =
        typeof g.sentence?.ms_since_user === 'number'
          ? g.sentence.ms_since_user
          : totalMs != null && ttsMs != null
            ? totalMs - ttsMs
            : null;
      // STT is pre-Brain — pull it out so it renders as its own top-level segment.
      const sttMs = g.stages.has('stt') ? (g.stages.get('stt') as number) : null;
      const stages: StageTiming[] = Array.from(g.stages.entries())
        .filter(([stage]) => stage !== 'stt')
        .map(([stage, ms]) => ({ stage, ms }))
        .sort((a, b) => {
          const ia = STAGE_ORDER.indexOf(a.stage);
          const ib = STAGE_ORDER.indexOf(b.stage);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
      return { turn, sttMs, totalMs, brainMs, ttsMs, stages };
    });
};

const outcome = (row: ConversationRow): { label: string; cls: string } | null => {
  const b = String(row.metadata?.extracted_fields?.bookingStatus || '').toLowerCase();
  if (b === 'booked' || b === 'complete' || b === 'completed') return { label: 'Booked', cls: 'bg-green-100 text-green-700' };
  if (b === 'incomplete') return { label: 'Incomplete', cls: 'bg-amber-100 text-amber-700' };
  return null;
};

/** Per-turn response-time breakdown for one conversation, computed from the
 *  latency marks in workflow_run_events (T0/T1/T2). "Brain" = routing + guards +
 *  retrieval + LLM to first sentence; "TTS" = first sentence → first audio. */
const StatsPanel: React.FC<{ events: RunEvent[]; loading: boolean }> = ({ events, loading }) => {
  if (loading) {
    return <div className="py-8 text-center text-sm text-gray-400">Loading timing data…</div>;
  }
  const turns = computeTurnStats(events);
  if (turns.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No timing data recorded for this conversation. Only calls placed after per-turn timing was
        enabled will have a breakdown.
      </div>
    );
  }
  // End-to-end = STT (Deepgram endpointing) + Brain + TTS — what the caller feels.
  const e2e = (t: TurnStat): number | null =>
    t.totalMs == null ? null : (t.sttMs ?? 0) + t.totalMs;
  const validE = turns.map(e2e).filter((x): x is number => typeof x === 'number');
  const avg = validE.length ? validE.reduce((s, x) => s + x, 0) / validE.length : null;
  const anyStt = turns.some((t) => t.sttMs != null);

  return (
    <div className="flex flex-col gap-3">
      {/* Call-level summary */}
      <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
        <span className="text-xs text-gray-500">Avg response time</span>
        <span className="text-sm font-semibold text-gray-900">{fmtMs(avg)}</span>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-gray-500">
        {anyStt ? <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />STT</span> : null}
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-500" />Brain</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />TTS</span>
      </div>

      {/* Per-turn cards */}
      {turns.map((t) => {
        const stt = t.sttMs ?? 0;
        const brain = t.brainMs ?? 0;
        const tts = t.ttsMs ?? 0;
        const denom = stt + brain + tts;
        const pct = (v: number) => (denom ? (v / denom) * 100 : 0);
        // "Other" = brain time not attributed to a measured sub-step (glue, prompt
        // assembly, etc.).
        const stagesSum = t.stages.reduce((s, x) => s + x.ms, 0);
        const otherMs = t.brainMs != null ? Math.max(0, t.brainMs - stagesSum) : null;
        const barDenom = t.brainMs && t.brainMs > 0 ? t.brainMs : stagesSum || 1;
        return (
          <div key={t.turn} className="rounded-lg border border-gray-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Turn {t.turn}</span>
              <span className="text-sm font-semibold text-gray-900">{fmtMs(e2e(t))}</span>
            </div>
            {denom > 0 ? (
              <div className="mb-2 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
                {t.sttMs != null ? <div className="h-full bg-slate-400" style={{ width: `${pct(stt)}%` }} /> : null}
                <div className="h-full bg-indigo-500" style={{ width: `${pct(brain)}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${pct(tts)}%` }} />
              </div>
            ) : null}
            <div className="flex items-center justify-between text-xs text-gray-500">
              {t.sttMs != null ? <span>STT <span className="font-medium text-gray-700">{fmtMs(t.sttMs)}</span></span> : null}
              <span>Brain <span className="font-medium text-gray-700">{fmtMs(t.brainMs)}</span></span>
              <span>TTS <span className="font-medium text-gray-700">{fmtMs(t.ttsMs)}</span></span>
            </div>

            {/* Brain sub-step breakdown */}
            {t.stages.length > 0 ? (
              <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-2.5">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Inside Brain</div>
                {t.stages.map((s) => {
                  const meta = stageMeta(s.stage);
                  return (
                    <div key={s.stage} className="flex items-center gap-2">
                      <span className="w-24 shrink-0 text-xs text-gray-500">{meta.label}</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <span className={`block h-full ${meta.color}`} style={{ width: `${Math.min(100, (s.ms / barDenom) * 100)}%` }} />
                      </span>
                      <span className="w-14 shrink-0 text-right text-xs font-medium text-gray-700">{fmtMs(s.ms)}</span>
                    </div>
                  );
                })}
                {otherMs != null && otherMs > 50 ? (
                  <div className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-gray-400">Other</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <span className="block h-full bg-gray-300" style={{ width: `${Math.min(100, (otherMs / barDenom) * 100)}%` }} />
                    </span>
                    <span className="w-14 shrink-0 text-right text-xs font-medium text-gray-500">{fmtMs(otherMs)}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

/** Full workflow_run_events log for a conversation, as a per-event accordion —
 *  routing decisions (intent_resolved/branch_selected), retrieval, tool calls,
 *  offers, escalations, timings. Lets you debug a call (esp. remote) from the DB
 *  without server access. */
const evTime = (e: RunEvent): string => (e.payload?.at as string) || e.createdAt;
const compactPayload = (p: any): string => {
  if (!p || typeof p !== 'object') return '';
  return Object.keys(p)
    .filter((k) => k !== 'at')
    .map((k) => {
      const v = (p as any)[k];
      return `${k}=${v && typeof v === 'object' ? JSON.stringify(v) : String(v)}`;
    })
    .join('  ');
};
const eventBadgeCls = (type: string): string => {
  if (type.startsWith('intent') || type.startsWith('branch')) return 'bg-violet-100 text-violet-700';
  if (type.startsWith('retrieval') || type.startsWith('stage')) return 'bg-sky-100 text-sky-700';
  if (type.includes('tool')) return 'bg-amber-100 text-amber-700';
  if (type.includes('escalat') || type.includes('handoff') || type.includes('fallback') || type.includes('unavailable')) return 'bg-rose-100 text-rose-700';
  if (type.includes('offer') || type.includes('booked')) return 'bg-emerald-100 text-emerald-700';
  return 'bg-gray-100 text-gray-600';
};

const EventLogPanel: React.FC<{ events: RunEvent[]; loading: boolean }> = ({ events, loading }) => {
  if (loading) return <div className="py-4 text-center text-xs text-gray-400">Loading events…</div>;
  if (!events.length) return <div className="py-4 text-center text-xs text-gray-400">No events recorded for this conversation.</div>;
  const sorted = [...events].sort((a, b) => new Date(evTime(a)).getTime() - new Date(evTime(b)).getTime());
  const t0 = new Date(evTime(sorted[0])).getTime();
  return (
    <div className="flex flex-col gap-1">
      {sorted.map((e) => {
        const rel = (new Date(evTime(e)).getTime() - t0) / 1000;
        return (
          <details key={e.id} className="rounded-md border border-gray-100">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50">
              <span className="w-12 shrink-0 tabular-nums text-gray-400">+{rel.toFixed(2)}s</span>
              <span className={`shrink-0 rounded px-1.5 py-0.5 font-medium ${eventBadgeCls(e.type)}`}>{e.type}</span>
              <span className="truncate text-gray-500">{compactPayload(e.payload)}</span>
            </summary>
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-b-md bg-gray-50 px-2.5 py-2 text-[11px] leading-relaxed text-gray-600">
{JSON.stringify(e.payload ?? {}, null, 2)}
            </pre>
          </details>
        );
      })}
    </div>
  );
};

export const WorkflowConversationsDrawer: React.FC<Props> = ({ open, workflowId, workflowName, onClose }) => {
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState<ConversationRow | null>(null);
  const [transcript, setTranscript] = useState<TranscriptMsg[]>([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [showEventLog, setShowEventLog] = useState(false);
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const loadList = useCallback(async () => {
    if (!workflowId) return;
    setLoadingList(true);
    try {
      const res = await workflowService.listRuns(workflowId, { limit: 50 } as any);
      setRows(((res as any)?.items ?? []) as ConversationRow[]);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load conversations');
    } finally {
      setLoadingList(false);
    }
  }, [workflowId]);

  useEffect(() => {
    if (open) { setSelected(null); setRecordingUrl(null); setShowStats(false); setShowEventLog(false); setEvents([]); setEventsLoaded(false); void loadList(); }
  }, [open, loadList]);

  // Fetch the run's workflow_run_events once; shared by the stats view and the
  // debug event-log accordion.
  const ensureEvents = useCallback(async () => {
    if (!selected || eventsLoaded) return;
    setLoadingEvents(true);
    try {
      setEvents((await workflowService.getRunEvents(selected.id)) as RunEvent[]);
      setEventsLoaded(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load event log');
    } finally {
      setLoadingEvents(false);
    }
  }, [selected, eventsLoaded]);

  const toggleStats = useCallback(async () => {
    if (!selected) return;
    const next = !showStats;
    setShowStats(next);
    if (next) void ensureEvents();
  }, [selected, showStats, ensureEvents]);

  const toggleEventLog = useCallback(async () => {
    const next = !showEventLog;
    setShowEventLog(next);
    if (next) void ensureEvents();
  }, [showEventLog, ensureEvents]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') (selected ? setSelected(null) : onClose()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selected, onClose]);

  const openConversation = useCallback(async (row: ConversationRow) => {
    setSelected(row);
    setTranscript([]);
    setRecordingUrl(null);
    setShowStats(false);
    setShowEventLog(false);
    setEvents([]);
    setEventsLoaded(false);
    setLoadingTranscript(true);
    try {
      const [msgs, detail] = await Promise.all([
        row.conversationId ? workflowService.getConversationTranscript(row.conversationId) : Promise.resolve([]),
        workflowService.getRunById(row.id),
      ]);
      setTranscript(msgs);
      const rec = (detail?.metadata as any)?.recording;
      if (rec?.s3Key && rec?.s3Bucket) {
        const { url } = await fileService.getImageFromkey(rec.s3Key, rec.s3Bucket, 'audio/mpeg');
        setRecordingUrl(url ?? null);
      } else if (rec?.presignedUrl) {
        // fallback for runs recorded before the on-demand URL change
        setRecordingUrl(rec.presignedUrl);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load transcript');
    } finally {
      setLoadingTranscript(false);
    }
  }, []);

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <aside
        role="dialog"
        aria-label="Workflow conversations"
        aria-hidden={!open}
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-200 ease-out ${open ? 'translate-x-0' : 'pointer-events-none translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {selected ? (
              <Button tone="custom" type="button" onClick={() => { setSelected(null); setRecordingUrl(null); setShowStats(false); setShowEventLog(false); setEvents([]); setEventsLoaded(false); }} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Back to list">
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
            ) : null}
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-gray-900">
                {selected ? channelMeta(selected).label : 'Conversations'}
              </h3>
              <p className="truncate text-xs text-gray-400">{workflowName || 'This workflow'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {selected ? (
              <Button tone="custom"
                type="button"
                onClick={toggleStats}
                className={`rounded-md p-1.5 ${showStats ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
                aria-label="Response time breakdown"
                title="Response time breakdown"
              >
                <ChartBarIcon className="h-4 w-4" />
              </Button>
            ) : null}
            {!selected ? (
              <Button tone="icon-ghost" type="button" onClick={loadList} className="rounded-md p-1.5" aria-label="Refresh">
                <ArrowPathIcon className={`h-4 w-4 ${loadingList ? 'animate-spin' : ''}`} />
              </Button>
            ) : null}
            <Button tone="icon-ghost" type="button" onClick={onClose} className="rounded-md p-1.5" aria-label="Close">
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!selected ? (
            // ── List ──
            loadingList && rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading conversations…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No conversations yet. They’ll appear here after calls or chats run through this workflow.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const cm = channelMeta(row);
                  const oc = outcome(row);
                  return (
                    <li key={row.id}>
                      <Button tone="custom"
                        type="button"
                        onClick={() => openConversation(row)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">{cm.icon}</span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm font-medium text-gray-900">{cm.label}</span>
                            <span className="shrink-0 text-xs text-gray-400">{relativeTime(row.createdAt)}</span>
                          </span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                            {durationLabel(row) ? (
                              <span className="inline-flex items-center gap-1"><ClockIcon className="h-3.5 w-3.5" />{durationLabel(row)}</span>
                            ) : null}
                            {row.stepCount ? <span>{row.stepCount} turns</span> : null}
                            {oc ? <span className={`rounded-full px-2 py-0.5 font-medium ${oc.cls}`}>{oc.label}</span> : null}
                            <span className={`rounded-full px-2 py-0.5 ${row.status === 'completed' ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'}`}>{row.status}</span>
                          </span>
                        </span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            // ── Detail (transcript) ──
            <div className="flex flex-col gap-3 p-4">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                {durationLabel(selected) ? (
                  <span className="inline-flex items-center gap-1 font-medium text-gray-700"><ClockIcon className="h-3.5 w-3.5" />{durationLabel(selected)}</span>
                ) : null}
                <span>{[selected.status, relativeTime(selected.createdAt)].filter(Boolean).join(' · ')}</span>
                {outcome(selected) ? <span className={`rounded-full px-2 py-0.5 font-medium ${outcome(selected)!.cls}`}>{outcome(selected)!.label}</span> : null}
              </div>
              {showStats ? (
                <StatsPanel events={events} loading={loadingEvents} />
              ) : (
              <>
              {recordingUrl ? (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                    <MusicalNoteIcon className="h-3.5 w-3.5" />
                    Call Recording
                  </div>
                  <audio controls src={recordingUrl} className="w-full" preload="metadata">
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ) : null}
              {loadingTranscript ? (
                <div className="py-8 text-center text-sm text-gray-400">Loading transcript…</div>
              ) : transcript.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-400">Transcript not recorded for this conversation.</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {transcript.map((m) => {
                    const isUser = String(m.senderType).toLowerCase() === 'user';
                    return (
                      <div
                        key={m.id}
                        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${isUser ? 'self-end bg-blue-600 text-white' : 'self-start bg-gray-100 text-gray-800'}`}
                      >
                        {renderChatMarkdown(String(m.message ?? ''))}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Debug event log — routing/tool/timing marks from workflow_run_events */}
              <div className="rounded-lg border border-gray-100">
                <Button tone="custom"
                  type="button"
                  onClick={toggleEventLog}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <ChartBarIcon className="h-3.5 w-3.5" />
                    Debug event log{events.length ? ` (${events.length})` : ''}
                  </span>
                  <span className={`text-gray-400 transition-transform ${showEventLog ? 'rotate-90' : ''}`}>›</span>
                </Button>
                {showEventLog ? (
                  <div className="border-t border-gray-100 p-2">
                    <EventLogPanel events={events} loading={loadingEvents} />
                  </div>
                ) : null}
              </div>
              </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default WorkflowConversationsDrawer;
